<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, toRef, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { VtkViewContext } from '@/src/components/vtk/context';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useImage } from '@/src/composables/useCurrentImage';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import type { LPSAxis } from '@/src/types/lps';
import { useDentalTaskStore } from './taskStore';
import { useDentalCbctStore } from './cbctStore';
import { useDentalSceneStore } from './sceneStore';
const props = defineProps<{ viewId: string; imageId?: string | null; axis: LPSAxis }>();
const view = inject(VtkViewContext)!; const task = useDentalTaskStore(); const cbct = useDentalCbctStore(); const scene = useDentalSceneStore();
const { imageData, metadata } = useImage(toRef(props, 'imageId'));
const { slice } = useSliceConfig(toRef(props, 'viewId'), toRef(props, 'imageId'));
const overlay = ref<SVGSVGElement>(); const markers = ref<{ id: string; name: string; x: number; y: number }[]>([]); let frame = 0;
const inPlane = computed(() => {
  const image = imageData.value; const axis = metadata.value?.lpsOrientation[props.axis];
  if (!image || axis === undefined || slice.value == null || task.task?.kind !== 'cbct-landmark' || !scene.landmarksVisible) return [];
  return cbct.landmarks.filter(point => Math.abs(image.worldToIndex(point.position)[axis] - slice.value!) <= .51);
});
function update() {
  cancelAnimationFrame(frame); frame = requestAnimationFrame(() => {
    const box = overlay.value?.getBoundingClientRect(); const size = view.renderWindowView.getSize();
    if (!box || !size[0] || !size[1]) return;
    markers.value = inPlane.value.map(point => {
      const [x,y] = view.renderWindowView.worldToDisplay(...point.position, view.renderer);
      return {id: point.id, name: point.name, x: x * box.width / size[0], y: box.height - y * box.height / size[1]};
    });
  });
}
watch(inPlane, update, { deep: true, immediate: true }); onVTKEvent(view.renderer.getActiveCamera(), 'onModified', update);
useResizeObserver(overlay, update); onBeforeUnmount(() => cancelAnimationFrame(frame));
</script>
<template><svg ref="overlay" class="landmark-slice-overlay" data-testid="landmark-slice-overlay"><g v-for="point in markers" :key="point.id" :transform="`translate(${point.x} ${point.y})`" data-testid="slice-landmark"><circle r="5" /><path d="M-8 0H8 M0-8V8" /><text v-if="scene.landmarkLabels" x="10" y="-8">{{ point.name }}</text></g></svg></template>
<style scoped>
.landmark-slice-overlay { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 2; } circle { fill: #79b7d533; stroke: #79b7d5; stroke-width: 1.5px; } path { stroke: #a9d5ec; stroke-width: 1px; } text { fill: #a9d5ec; font-size: 11px; stroke: #0b1520; stroke-width: 2px; paint-order: stroke; }
</style>
